import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { motion } from 'motion/react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import InternalDashboard from './InternalDashboard';
import { InternalOrgDetail } from './InternalDashboard';
import InternalDemo from './InternalDemo';
import InternalPackages from './InternalPackages';
import InternalLoadTest from './InternalLoadTest';
import InternalTestingCheck from './InternalTestingCheck';
import InternalUsageTracking from './InternalUsageTracking';
import InternalBlog from './InternalBlog';
import AIHealerAdmin from './AIHealerAdmin';
import DemoCalendarView from './DemoCalendarView';
import {
  LayoutDashboard,
  Building2,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  LogOut,
  Sparkles,
  Users,
  FileCheck,
  Video,
  UserPlus,
  ClipboardList,
  Mail,
  TrendingUp,
  LifeBuoy,
  X,
  CalendarPlus,
  CalendarDays,
  Package,
  Layers,
  Activity,
  BarChart3,
  FileText,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import AnimatedModal from '../../components/ui/AnimatedModal';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TooltipProvider } from '../../components/ui/ui/tooltip';

const SAMPLE_NAMES = [
  'Acme Corp',
  'TechVentures Ltd',
  'Global Solutions Inc',
  'Nexus Industries',
  'Summit Consulting',
  'Horizon Labs',
  'Pioneer Systems',
  'Apex Group',
];
const SAMPLE_FIRST = ['James', 'Sarah', 'Michael', 'Priya', 'David', 'Emma', 'Raj', 'Lisa'];
const SAMPLE_LAST = ['Smith', 'Chen', 'Johnson', 'Patel', 'Williams', 'Kumar', 'Brown', 'Lee'];
const SAMPLE_CITIES = [
  'Mumbai',
  'New York',
  'London',
  'Sydney',
  'Toronto',
  'Berlin',
  'Singapore',
  'Dubai',
];
const SAMPLE_STATES = [
  'Maharashtra',
  'California',
  'England',
  'NSW',
  'Ontario',
  'Berlin',
  'Central',
  'Dubai',
];
const SAMPLE_INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Retail',
  'Manufacturing',
  'Consulting',
];
const SAMPLE_DESCRIPTIONS = [
  'Leading provider of innovative solutions.',
  'We help businesses grow and scale.',
  'Driving digital transformation worldwide.',
];

const CHART_METRIC_CONFIG = {
  llmCalls: { label: 'Calls', color: '#2563eb', format: (v) => v?.toLocaleString(), yPrefix: '' },
  llmInputTokens: { label: 'Input Tokens', color: '#3b82f6', format: (v) => v?.toLocaleString(), yPrefix: '' },
  llmOutputTokens: { label: 'Output Tokens', color: '#60a5fa', format: (v) => v?.toLocaleString(), yPrefix: '' },
  llmCostUsd: { label: 'Est. OpenAI Cost', color: '#f59e0b', format: (v) => `$${Number(v).toFixed(2)}`, yPrefix: '$' },
  interviewSessions: { label: 'Sessions', color: '#10b981', format: (v) => v?.toLocaleString(), yPrefix: '' },
  interviewDurationMinutes: { label: 'Duration (min)', color: '#34d399', format: (v) => Number(v)?.toFixed(1), yPrefix: '' },
  interviewCostUsd: { label: 'Est. LiveKit Cost', color: '#f59e0b', format: (v) => `$${Number(v).toFixed(2)}`, yPrefix: '$' },
};

function getRandomSampleOrg() {
  const name = SAMPLE_NAMES[Math.floor(Math.random() * SAMPLE_NAMES.length)];
  const slug =
    name
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '') +
    '-' +
    Math.random().toString(36).slice(2, 6);
  const legalName = name + ' Limited';
  const idx = Math.floor(Math.random() * 1000);
  const email = `contact-${idx}@${slug.split('-')[0]}.com`;
  const phone =
    '+' +
    Math.floor(Math.random() * 900 + 100) +
    ' ' +
    Math.floor(Math.random() * 900 + 100) +
    ' ' +
    Math.floor(Math.random() * 9000000 + 1000000);
  const website = `https://www.${slug.split('-')[0]}.com`;
  const addressLine1 =
    Math.floor(Math.random() * 9999) +
    1 +
    ' Sample Street, Building ' +
    (Math.floor(Math.random() * 20) + 1);
  const city = SAMPLE_CITIES[Math.floor(Math.random() * SAMPLE_CITIES.length)];
  const state = SAMPLE_STATES[Math.floor(Math.random() * SAMPLE_STATES.length)];
  const countryKeys = ['IN', 'US', 'GB', 'AU', 'CA', 'DE', 'SG', 'AE'];
  const country = countryKeys[Math.floor(Math.random() * countryKeys.length)];
  const postalCode = String(Math.floor(Math.random() * 90000) + 10000);
  const industry = SAMPLE_INDUSTRIES[Math.floor(Math.random() * SAMPLE_INDUSTRIES.length)];
  const companySizes = ['1-10', '11-50', '51-200', '201-500', '501+'];
  const companySize = companySizes[Math.floor(Math.random() * companySizes.length)];
  const description = SAMPLE_DESCRIPTIONS[Math.floor(Math.random() * SAMPLE_DESCRIPTIONS.length)];
  const plans = ['starter', 'core', 'custom'];
  const plan = plans[Math.floor(Math.random() * plans.length)];
  const totalAiCredits = null;
  const status = 'active';
  const firstName = SAMPLE_FIRST[Math.floor(Math.random() * SAMPLE_FIRST.length)];
  const lastName = SAMPLE_LAST[Math.floor(Math.random() * SAMPLE_LAST.length)];
  return {
    name,
    slug,
    legalName,
    email,
    phone,
    website,
    addressLine1,
    city,
    state,
    country,
    postalCode,
    industry,
    companySize,
    description,
    plan,
    totalAiCredits,
    status,
    firstName,
    lastName,
    creditCostInterview: '1',
    creditCostAssessment: '0.5',
    creditCostResume: '0',
  };
}

const INITIAL_ORG_FORM = {
  name: '',
  slug: '',
  legalName: '',
  email: '',
  phone: '',
  website: '',
  addressLine1: '',
  city: '',
  state: '',
  country: '',
  postalCode: '',
  industry: '',
  companySize: '',
  description: '',
  plan: 'starter',
  billingPeriod: 'monthly',
  status: 'active',
  totalAiCredits: '',
  firstName: '',
  lastName: '',
  // PAYG fields
  ondemandEnabled: true,
  ondemandLimit: '',
  ondemandRateVideo: '',
  ondemandRateAudio: '',
  ondemandRateAssessment: '',
  ondemandRateResume: '',
  // Quota overrides (used for custom/enterprise plans)
  quotaVideoInterviews: '',
  quotaAudioInterviews: '',
  quotaAssessments: '',
  quotaResumeScores: '',
  planTotalUsers: '',
  // Custom credit costs overrides
  creditCostInterview: '1',
  creditCostAssessment: '0.5',
  creditCostResume: '0',
};

// Plan config for quota display
const PLAN_QUOTAS = {
  starter: { credits: 100, users: 2, price: '₹24,999/mo' },
  core: { credits: 250, users: 10, price: '₹44,999/mo' },
};

const PLAN_RATES = {
  starter: { interview: 300, assessment: 150 },
  core: { interview: 250, assessment: 125 },
};

const PLANS = [
  { value: 'starter', label: 'Starter (₹24,999/mo)' },
  { value: 'core', label: 'Core (₹44,999/mo)' },
  { value: 'custom', label: 'Enterprise (Custom)' },
];

const COMPANY_SIZES = [
  { value: '', label: 'Select size' },
  { value: '1-10', label: '1–10' },
  { value: '11-50', label: '11–50' },
  { value: '51-200', label: '51–200' },
  { value: '201-500', label: '201–500' },
  { value: '501+', label: '501+' },
];

const COUNTRIES = [
  { value: '', label: 'Select country' },
  { value: 'IN', label: 'India' },
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'CA', label: 'Canada' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'SG', label: 'Singapore' },
  { value: 'AE', label: 'UAE' },
  { value: 'OTHER', label: 'Other' },
];

const inputCls =
  'w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 outline-none transition';
const labelCls = 'block text-sm font-medium text-gray-800 mb-1.5';

function generateOrgSlug(name) {
  if (!name) return '';
  const base = name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
  const random = Math.random().toString(36).slice(2, 8);
  return `${base || 'org'}-${random}`;
}

const Internal = () => {
  const { user, logout, isPlatformSuperAdmin, isBlogAdmin } = useAuth();
  // Blog managers (not full super-admins) are confined to the Blogs tab.
  const blogOnly = isBlogAdmin && !isPlatformSuperAdmin;
  const navigate = useNavigate();
  const location = useLocation();
  const orgSlugMatch = location.pathname.match(/^\/internal\/org\/([^/]+)$/);
  // Org-detail is a super-admin-only view; never resolve it for blog-only users.
  const orgSlug = orgSlugMatch && !blogOnly ? orgSlugMatch[1] : null;

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeView, setActiveView] = useState(blogOnly ? 'blogs' : 'dashboard');
  const [orgForm, setOrgForm] = useState(INITIAL_ORG_FORM);
  const [orgInvoiceFile, setOrgInvoiceFile] = useState(null);
  const [orgInvoiceUploading, setOrgInvoiceUploading] = useState(false);
  const [orgSubmitMessage, setOrgSubmitMessage] = useState(null);
  const [orgSubmitLoading, setOrgSubmitLoading] = useState(false);
  const [orgFieldErrors, setOrgFieldErrors] = useState(null);
  const [showFillRandomButton, setShowFillRandomButton] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [timeSeriesLoading, setTimeSeriesLoading] = useState(false);
  const [orgOverview, setOrgOverview] = useState([]);
  const [orgOverviewLoading, setOrgOverviewLoading] = useState(false);
  const [orgOverviewError, setOrgOverviewError] = useState(null);
  const [expandedOrgId, setExpandedOrgId] = useState(null);

  // AI & LLM usage (internal)
  const [llmUsage, setLlmUsage] = useState(null);
  const [llmUsageLoading, setLlmUsageLoading] = useState(false);
  const [llmUsageError, setLlmUsageError] = useState(null);
  const [llmTimeFilter, setLlmTimeFilter] = useState('pastMonth');
  const [llmCustomFrom, setLlmCustomFrom] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10); });
  const [llmCustomTo, setLlmCustomTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [aiInterviewUsage, setAiInterviewUsage] = useState(null);
  const [aiInterviewUsageLoading, setAiInterviewUsageLoading] = useState(false);
  const [aiInterviewUsageError, setAiInterviewUsageError] = useState(null);
  const [aiInterviewTimeFilter, setAiInterviewTimeFilter] = useState('pastMonth');
  const [aiInterviewCustomFrom, setAiInterviewCustomFrom] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10); });
  const [aiInterviewCustomTo, setAiInterviewCustomTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [usageTimeSeries, setUsageTimeSeries] = useState(null);
  const [usageTimeSeriesLoading, setUsageTimeSeriesLoading] = useState(false);

  // Support requests (internal admin)
  const [supportFilters, setSupportFilters] = useState({
    status: '',
    category: '',
    priority: '',
  });
  const [supportRequests, setSupportRequests] = useState([]);
  const [supportTotal, setSupportTotal] = useState(0);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportError, setSupportError] = useState(null);
  const [supportDetail, setSupportDetail] = useState(null);
  const [supportDetailLoading, setSupportDetailLoading] = useState(false);
  const [supportNotesLocal, setSupportNotesLocal] = useState('');
  const [supportSaving, setSupportSaving] = useState(false);
  const [supportReplyText, setSupportReplyText] = useState('');
  const [supportReplySubmitting, setSupportReplySubmitting] = useState(false);
  const [isRightEdgeHovered, setIsRightEdgeHovered] = useState(false);
  const [statsModalOpen, setStatsModalOpen] = useState(null); // 'organisations' | 'org-users' | 'job-postings' | ... | 'llm-by-org' | 'ai-interview-by-job' | 'ai-interview-by-org'
  const [chartModal, setChartModal] = useState(null); // null | { metric: 'llmCalls' | 'llmInputTokens' | ... | 'interviewCostUsd' }

  // Resource videos (internal admin)
  const [resourceVideos, setResourceVideos] = useState([]);
  const [resourceVideosLoading, setResourceVideosLoading] = useState(false);
  const [resourceForm, setResourceForm] = useState({
    title: '',
    description: '',
    minutes: '',
    level: 'Beginner',
    category: 'getting-started',
    tags: '',
  });
  const [resourceThumbnail, setResourceThumbnail] = useState(null);
  const [resourceVideoFile, setResourceVideoFile] = useState(null);
  const [resourceSubmitLoading, setResourceSubmitLoading] = useState(false);
  const [resourceSubmitError, setResourceSubmitError] = useState(null);

  const fetchDashboardStats = () => {
    setDashboardLoading(true);
    setDashboardError(null);
    axios
      .get('/api/internal/dashboard-stats')
      .then((res) => setDashboardStats(res.data))
      .catch((err) => setDashboardError(err.response?.data?.message || 'Failed to load stats'))
      .finally(() => setDashboardLoading(false));
  };

  const fetchOrgOverview = () => {
    setOrgOverviewLoading(true);
    setOrgOverviewError(null);
    axios
      .get('/api/internal/organisations-overview')
      .then((res) => {
        const list = res.data?.organisations || [];
        setOrgOverview(list);
        if (!expandedOrgId && list.length) {
          setExpandedOrgId(list[0].id);
        }
      })
      .catch((err) => {
        setOrgOverviewError(
          err.response?.data?.message || 'Failed to load organisation overview'
        );
        setOrgOverview([]);
      })
      .finally(() => setOrgOverviewLoading(false));
  };

  const fetchTimeSeriesStats = () => {
    setTimeSeriesLoading(true);
    axios
      .get('/api/internal/time-series-stats?days=30')
      .then((res) => setTimeSeriesData(res.data || []))
      .catch((err) => {
        console.error('Failed to load time series stats:', err);
        setTimeSeriesData([]);
      })
      .finally(() => setTimeSeriesLoading(false));
  };

  const fetchLlmUsage = () => {
    setLlmUsageLoading(true);
    setLlmUsageError(null);
    const now = new Date();
    let from = null;
    let to = null;
    if (llmTimeFilter === 'pastDay') {
      const start = new Date(now); start.setDate(start.getDate() - 1);
      from = start.toISOString().slice(0, 10); to = now.toISOString().slice(0, 10);
    } else if (llmTimeFilter === 'pastWeek') {
      const start = new Date(now); start.setDate(start.getDate() - 7);
      from = start.toISOString().slice(0, 10); to = now.toISOString().slice(0, 10);
    } else if (llmTimeFilter === 'pastMonth') {
      const start = new Date(now); start.setMonth(start.getMonth() - 1);
      from = start.toISOString().slice(0, 10); to = now.toISOString().slice(0, 10);
    } else if (llmTimeFilter === 'custom') {
      from = llmCustomFrom || null; to = llmCustomTo || null;
    }
    const params = new URLSearchParams({ groupBy: 'org' });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    axios
      .get(`/api/internal/llm-usage?${params.toString()}`)
      .then((res) => setLlmUsage(res.data))
      .catch((err) => {
        setLlmUsageError(err.response?.data?.message || 'Failed to load LLM usage');
        setLlmUsage(null);
      })
      .finally(() => setLlmUsageLoading(false));
  };

  const fetchAiInterviewUsage = () => {
    setAiInterviewUsageLoading(true);
    setAiInterviewUsageError(null);
    const now = new Date();
    let from = null;
    let to = null;
    if (aiInterviewTimeFilter === 'pastDay') {
      const start = new Date(now);
      start.setDate(start.getDate() - 1);
      from = start.toISOString().slice(0, 10);
      to = now.toISOString().slice(0, 10);
    } else if (aiInterviewTimeFilter === 'pastWeek') {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      from = start.toISOString().slice(0, 10);
      to = now.toISOString().slice(0, 10);
    } else if (aiInterviewTimeFilter === 'pastMonth') {
      const start = new Date(now);
      start.setMonth(start.getMonth() - 1);
      from = start.toISOString().slice(0, 10);
      to = now.toISOString().slice(0, 10);
    } else if (aiInterviewTimeFilter === 'custom') {
      from = aiInterviewCustomFrom || null;
      to = aiInterviewCustomTo || null;
    }
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    axios
      .get(`/api/internal/ai-interview-usage?${params.toString()}`)
      .then((res) => setAiInterviewUsage(res.data))
      .catch((err) => {
        setAiInterviewUsageError(err.response?.data?.message || 'Failed to load AI interview usage');
        setAiInterviewUsage(null);
      })
      .finally(() => setAiInterviewUsageLoading(false));
  };

  function getChartTimeParams(metric) {
    const isLlm = ['llmCalls', 'llmInputTokens', 'llmOutputTokens', 'llmCostUsd'].includes(metric);
    const timeFilter = isLlm ? llmTimeFilter : aiInterviewTimeFilter;
    const customFrom = isLlm ? llmCustomFrom : aiInterviewCustomFrom;
    const customTo = isLlm ? llmCustomTo : aiInterviewCustomTo;
    const granularity = timeFilter === 'pastDay' ? 'hourly' : timeFilter === 'pastMonth' ? 'weekly' : 'daily';
    const now = new Date();
    let from = null;
    let to = null;
    if (timeFilter === 'pastDay') {
      to = now.toISOString();
      const start = new Date(now); start.setHours(start.getHours() - 24);
      from = start.toISOString();
    } else if (timeFilter === 'pastWeek') {
      const start = new Date(now); start.setDate(start.getDate() - 7);
      from = start.toISOString().slice(0, 10); to = now.toISOString().slice(0, 10);
    } else if (timeFilter === 'pastMonth') {
      const start = new Date(now); start.setMonth(start.getMonth() - 1);
      from = start.toISOString().slice(0, 10); to = now.toISOString().slice(0, 10);
    } else if (timeFilter === 'custom') {
      from = customFrom || null; to = customTo || null;
    }
    return { from, to, granularity, timeFilter };
  }

  const fetchUsageTimeSeries = ({ from, to, granularity = 'daily' } = {}) => {
    setUsageTimeSeriesLoading(true);
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    params.set('granularity', granularity);
    axios
      .get(`/api/internal/usage-timeseries?${params.toString()}`)
      .then((res) => setUsageTimeSeries(res.data))
      .catch(() => setUsageTimeSeries(null))
      .finally(() => setUsageTimeSeriesLoading(false));
  };

  const dashboardFetchedRef = useRef(false);
  useEffect(() => {
    // Blog-only managers can't hit the super-admin dashboard endpoints (403) —
    // and never see the dashboard view — so skip these fetches for them.
    if (blogOnly) return;
    fetchDashboardStats();
    fetchTimeSeriesStats();
    fetchOrgOverview();
    fetchLlmUsage();
    fetchAiInterviewUsage();
  }, []);

  useEffect(() => {
    if (!dashboardFetchedRef.current) {
      dashboardFetchedRef.current = true;
      return;
    }
    if (activeView === 'dashboard') {
      fetchDashboardStats();
      fetchTimeSeriesStats();
      fetchOrgOverview();
      fetchLlmUsage();
      fetchAiInterviewUsage();
    }
  }, [activeView]);

  const llmFilterInitialized = useRef(false);
  useEffect(() => {
    if (activeView !== 'dashboard' || llmTimeFilter === 'custom') return;
    if (!llmFilterInitialized.current) { llmFilterInitialized.current = true; return; }
    fetchLlmUsage();
  }, [llmTimeFilter]);

  const aiInterviewFilterInitialized = useRef(false);
  useEffect(() => {
    if (activeView !== 'dashboard' || aiInterviewTimeFilter === 'custom') return;
    if (!aiInterviewFilterInitialized.current) {
      aiInterviewFilterInitialized.current = true;
      return;
    }
    fetchAiInterviewUsage();
  }, [aiInterviewTimeFilter]);

  useEffect(() => {
    if (!chartModal) return;
    fetchUsageTimeSeries(getChartTimeParams(chartModal.metric));
  }, [chartModal]);

  useEffect(() => {
    if (activeView !== 'support') return;
    setSupportLoading(true);
    setSupportError(null);
    const params = new URLSearchParams();
    if (supportFilters.status) params.set('status', supportFilters.status);
    if (supportFilters.category) params.set('category', supportFilters.category);
    if (supportFilters.priority) params.set('priority', supportFilters.priority);
    params.set('limit', '50');
    axios
      .get(`/api/internal/support-requests?${params.toString()}`)
      .then((res) => {
        setSupportRequests(res.data.requests || []);
        setSupportTotal(res.data.total ?? 0);
      })
      .catch((err) => {
        setSupportError(err.response?.data?.message || 'Failed to load support requests');
        setSupportRequests([]);
        setSupportTotal(0);
      })
      .finally(() => setSupportLoading(false));
  }, [activeView, supportFilters.status, supportFilters.category, supportFilters.priority]);

  useEffect(() => {
    if (activeView !== 'resources-admin') return;
    setResourceVideosLoading(true);
    axios
      .get('/api/internal/resources/videos')
      .then((res) => setResourceVideos(res.data.videos || []))
      .catch(() => setResourceVideos([]))
      .finally(() => setResourceVideosLoading(false));
  }, [activeView]);

  const fetchResourceVideos = () => {
    setResourceVideosLoading(true);
    axios
      .get('/api/internal/resources/videos')
      .then((res) => setResourceVideos(res.data.videos || []))
      .finally(() => setResourceVideosLoading(false));
  };

  const handleResourceSubmit = (e) => {
    e.preventDefault();
    const title = (resourceForm.title || '').trim();
    const minutes = Number(resourceForm.minutes);
    if (!title) {
      toast.error('Title is required');
      return;
    }
    if (!resourceVideoFile) {
      toast.error('Please select a video file');
      return;
    }
    if (Number.isNaN(minutes) || minutes < 0) {
      toast.error('Please enter a valid duration (minutes)');
      return;
    }
    setResourceSubmitError(null);
    setResourceSubmitLoading(true);
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', (resourceForm.description || '').trim());
    formData.append('minutes', String(minutes));
    formData.append('level', resourceForm.level || 'Beginner');
    formData.append('category', resourceForm.category || 'getting-started');
    formData.append('tags', (resourceForm.tags || '').trim());
    if (resourceThumbnail) formData.append('thumbnail', resourceThumbnail);
    formData.append('video', resourceVideoFile);
    axios
      .post('/api/internal/resources/videos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(() => {
        toast.success('Video added. It will appear on the Resources page.');
        setResourceForm({ title: '', description: '', minutes: '', level: 'Beginner', category: 'getting-started', tags: '' });
        setResourceThumbnail(null);
        setResourceVideoFile(null);
        setResourceSubmitError(null);
        fetchResourceVideos();
      })
      .catch((err) => {
        const msg = err.response?.data?.message || err.message || 'Upload failed';
        setResourceSubmitError(msg);
        toast.error(msg);
      })
      .finally(() => setResourceSubmitLoading(false));
  };

  const handleResourceVideoDelete = (id) => {
    if (!window.confirm('Delete this resource video? It will be removed from the Resources page.')) return;
    axios
      .delete(`/api/internal/resources/videos/${id}`)
      .then(() => {
        toast.success('Video removed');
        fetchResourceVideos();
      })
      .catch((err) => toast.error(err.response?.data?.message || 'Delete failed'));
  };

  const fetchSupportDetail = (id) => {
    if (!id) {
      setSupportDetail(null);
      setSupportNotesLocal('');
      setSupportReplyText('');
      return;
    }
    setSupportDetailLoading(true);
    setSupportDetail(null);
    setSupportReplyText('');
    axios
      .get(`/api/internal/support-requests/${id}`)
      .then((res) => {
        setSupportDetail(res.data.request);
        setSupportNotesLocal(res.data.request?.adminNotes ?? '');
      })
      .catch(() => {
        toast.error('Failed to load request');
        setSupportDetail(null);
      })
      .finally(() => setSupportDetailLoading(false));
  };

  const updateSupportStatus = async (id, status) => {
    setSupportSaving(true);
    try {
      const res = await axios.patch(`/api/internal/support-requests/${id}`, { status });
      setSupportDetail(res.data.request);
      setSupportRequests((prev) =>
        prev.map((r) => (r._id === id ? res.data.request : r))
      );
      toast.success('Status updated');
      fetchDashboardStats();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    } finally {
      setSupportSaving(false);
    }
  };

  const saveSupportNotes = async (id) => {
    if (!id) return;
    setSupportSaving(true);
    try {
      const res = await axios.patch(`/api/internal/support-requests/${id}`, {
        adminNotes: supportNotesLocal,
      });
      setSupportDetail(res.data.request);
      setSupportRequests((prev) =>
        prev.map((r) => (r._id === id ? res.data.request : r))
      );
      toast.success('Notes saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save notes');
    } finally {
      setSupportSaving(false);
    }
  };

  const supportAuthorName = (author) => {
    if (!author) return '—';
    return author.name || author.email || '—';
  };

  const supportFormatDateTime = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  };

  const submitSupportReply = async (id) => {
    const text = (supportReplyText || '').trim();
    if (!text || !id) return;
    setSupportReplySubmitting(true);
    try {
      const res = await axios.post(`/api/internal/support-requests/${id}/comments`, { text });
      setSupportDetail(res.data.request);
      setSupportRequests((prev) =>
        prev.map((r) => (r._id === id ? res.data.request : r))
      );
      setSupportReplyText('');
      toast.success('Reply sent (user will see this)');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reply');
    } finally {
      setSupportReplySubmitting(false);
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey) setShowFillRandomButton(true);
    };
    const onKeyUp = (e) => {
      if (!e.ctrlKey || !e.shiftKey) setShowFillRandomButton(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const fillRandomValues = () => {
    setOrgForm(getRandomSampleOrg());
    setOrgInvoiceFile(null);
    setOrgSubmitMessage(null);
    setOrgFieldErrors(null);
    toast.success('Form filled with sample values. Click Create organisation to submit.');
  };

  const updateOrgForm = (field, value) => {
    setOrgForm((prev) => ({ ...prev, [field]: value }));
    setOrgFieldErrors((prev) => {
      if (!prev || !prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleCreateOrgSubmit = async (e) => {
    e.preventDefault();
    setOrgSubmitLoading(true);
    setOrgSubmitMessage(null);
    setOrgFieldErrors(null);
    try {
      const res = await axios.post('/api/internal/create-organisation', orgForm);
      toast.success(
        res.data.message || 'Organisation created. Activation email sent to org admin.'
      );
      const subscriptionId = res.data?.subscription?.id;

      if (orgInvoiceFile && subscriptionId) {
        try {
          setOrgInvoiceUploading(true);
          const formData = new FormData();
          formData.append('invoice', orgInvoiceFile);
          await axios.post(
            `/api/internal/subscriptions/${subscriptionId}/invoice`,
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
            }
          );
          toast.success('Invoice uploaded');
        } catch (uploadErr) {
          const msg =
            uploadErr.response?.data?.message ||
            uploadErr.message ||
            'Invoice upload failed';
          toast.error(msg);
        } finally {
          setOrgInvoiceUploading(false);
        }
      }

      setOrgForm(INITIAL_ORG_FORM);
      setOrgInvoiceFile(null);
      setOrgSubmitMessage(JSON.stringify(res.data, null, 2));
      setOrgFieldErrors(null);
    } catch (err) {
      const validationErrors = err.response?.data?.errors;
      if (Array.isArray(validationErrors) && validationErrors.length) {
        const mapped = {};
        for (const ve of validationErrors) {
          const path = ve?.path || ve?.param;
          if (!path) continue;
          if (!mapped[path]) mapped[path] = ve?.msg || 'Invalid value';
        }
        setOrgFieldErrors(mapped);
        const firstKey = Object.keys(mapped)[0];
        toast.error(mapped[firstKey] || 'Please fix the highlighted fields');
        setOrgSubmitMessage(null);
        return;
      }

      const msg =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0]?.msg ||
        err.message ||
        'Failed to create organisation';
      toast.error(msg);
      setOrgSubmitMessage(err.response?.data ? JSON.stringify(err.response.data, null, 2) : null);
    } finally {
      setOrgSubmitLoading(false);
    }
  };

  const handleLogout = () => {
    logout(() => navigate('/login'));
  };

  if (!isPlatformSuperAdmin && !isBlogAdmin) {
    return null;
  }

  const allNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'usage-tracking', label: 'Usage Tracking', icon: BarChart3 },
    { id: 'demo', label: 'Demo Requests', icon: CalendarPlus },
    { id: 'demo-calendar', label: 'Demo Request Calendar', icon: CalendarDays },
    { id: 'create-organisation', label: 'Create Organisation', icon: Building2 },
    { id: 'packages', label: 'Package Creation', icon: Package },
    { id: 'client-config', label: 'Client Configurations', icon: Layers },
    { id: 'support', label: 'Support requests', icon: LifeBuoy },
    { id: 'resources-admin', label: 'Resources (videos)', icon: Video },
    { id: 'blogs', label: 'Blogs', icon: FileText },
    { id: 'load-test', label: 'Interview Load Test', icon: Activity },
    { id: 'testing-check', label: 'Merge Testing Check', icon: ClipboardList },
    { id: 'ai-healer', label: 'Run Tests', icon: Sparkles },
  ];
  // Blog managers only ever see the Blogs tab.
  const navItems = blogOnly ? allNavItems.filter((item) => item.id === 'blogs') : allNavItems;

  return (
    <div className="h-screen bg-[#F7F9FC] flex overflow-hidden">
      <aside
        className={cn(
          'flex flex-col bg-[#F7F9FC] border-r border-gray-200/80 shadow-sm transition-all duration-300 ease-in-out relative h-full',
          isCollapsed ? 'w-16' : 'w-56'
        )}
      >
        {/* Branding - match Sidebar.js */}
        {!isCollapsed && (
          <div className="pl-4 pr-4 py-5 border-b border-gray-100 bg-[#F7F9FC]">
            <div className="flex items-center">
              <img
                src="/logo.svg"
                alt="TalentCrux Logo"
                className="w-[64px] h-[64px] object-contain flex-shrink-0"
              />
              <span className="ml-1 font-bold text-xl text-gray-900 tracking-tight align-middle">
                TalentCrux
              </span>
            </div>
          </div>
        )}
        {isCollapsed && (
          <div className="flex items-center justify-center h-16 border-b border-gray-100 bg-[#F7F9FC] relative">
            <img
              src="/logo.svg"
              alt="TalentCrux Logo"
              className="w-[42px] h-[42px] object-contain"
            />
          </div>
        )}

        {/* Right-edge hover zone: collapse button */}
        {!isCollapsed && (
          <div
            onMouseEnter={() => setIsRightEdgeHovered(true)}
            onMouseLeave={() => setIsRightEdgeHovered(false)}
            className="absolute top-0 right-0 bottom-0 w-10 z-10 flex items-center justify-end pr-1"
          >
            <button
              type="button"
              onClick={() => setIsCollapsed(true)}
              className={cn(
                'p-2 rounded-l-lg rounded-r-none transition-all duration-200',
                'bg-white/95 hover:bg-white border border-r-0 border-gray-200/90 shadow-sm hover:shadow',
                'text-gray-500 hover:text-[#007BFF] hover:border-[#007BFF]/30',
                isRightEdgeHovered
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 translate-x-2 pointer-events-none'
              )}
              aria-label="Collapse sidebar"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          </div>
        )}
        {isCollapsed && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
            <button
              type="button"
              onClick={() => setIsCollapsed(false)}
              className={cn(
                'p-1.5 rounded-lg transition-all duration-200',
                'hover:bg-[#007BFF]/10 active:bg-[#007BFF]/20',
                'text-gray-500 hover:text-[#007BFF]',
                'hover:shadow-sm border border-transparent hover:border-[#007BFF]/30'
              )}
              aria-label="Expand sidebar"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto py-4">
          <div className="pl-4 pr-2 space-y-1">
            {navItems.map((item) => {
              const active = activeView === item.id;
              return (
                <button
                  key={item.id}
                  data-testid={`internal-nav-${item.id}`}
                  type="button"
                  onClick={() => {
                    // If we are currently viewing a routed org detail (/internal/org/:slug),
                    // we must navigate back to `/internal` or the org detail view will remain mounted.
                    setActiveView(item.id);
                    if (orgSlug) navigate('/internal');
                  }}
                  className={cn(
                    'flex items-center w-full text-sm font-medium transition-all duration-200 rounded-r-lg relative pl-4 pr-3 py-2.5 text-left',
                    isCollapsed ? 'justify-center rounded-lg' : '',
                    active
                      ? 'bg-[#007BFF]/10 text-[#007BFF]'
                      : 'text-gray-600 hover:bg-[#007BFF]/5 hover:text-gray-900'
                  )}
                >
                  {active && !isCollapsed && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-[#007BFF] rounded-r-full" />
                  )}
                  <item.icon
                    className={cn(
                      'w-5 h-5 flex-shrink-0',
                      active ? 'text-[#007BFF]' : 'text-gray-600',
                      !isCollapsed && 'mr-3'
                    )}
                  />
                  {!isCollapsed && (
                    <span className={cn('flex-1 min-w-0', active && 'font-semibold')}>
                      {item.label}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        <div className="border-t border-gray-200 pl-4 pr-4 py-4">
          {!isCollapsed && (
            <>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white text-sm font-semibold flex-shrink-0">
                  {user?.name
                    ? user.name.charAt(0).toUpperCase()
                    : user?.email?.charAt(0).toUpperCase() || 'I'}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4 flex-shrink-0" />
                <span>Sign out</span>
              </button>
            </>
          )}
          {isCollapsed && (
            <div className="flex flex-col items-center">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white text-sm font-semibold mb-2">
                {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="p-2 rounded-md text-red-600 hover:bg-red-50"
                aria-label="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      <main
        id="main-content"
        className="flex-1 min-w-0 flex flex-col bg-[#F7F9FC] overflow-y-auto"
        style={{ padding: 'clamp(0.75rem, 1.5vw, 1.5rem)' }}
      >
        <TooltipProvider delayDuration={200} skipDelayDuration={0}>
          {orgSlug ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="min-h-full w-full"
              style={{ padding: 'clamp(0.75rem, 1.5vw, 1.5rem)' }}
            >
              <InternalOrgDetail
                slug={orgSlug}
                onBack={() => navigate('/internal')}
                setActiveView={setActiveView}
              />
            </motion.div>
          ) : activeView === 'dashboard' ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="min-h-full w-full"
              style={{ padding: 'clamp(0.75rem, 1.5vw, 1.5rem)' }}
            >
              <InternalDashboard
                dashboardStats={dashboardStats}
                orgOverview={orgOverview}
                orgOverviewLoading={orgOverviewLoading}
                orgOverviewError={orgOverviewError}
                setActiveView={setActiveView}
                navigate={navigate}
              />
            </motion.div>
          ) : (
            <></>
          )}

          {activeView === 'usage-tracking' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="min-h-full w-full"
              style={{ padding: 'clamp(0.75rem, 1.5vw, 1.5rem)' }}
            >
              <InternalUsageTracking />
            </motion.div>
          )}

          {activeView === 'demo' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="min-h-full w-full"
              style={{ padding: 'clamp(0.75rem, 1.5vw, 1.5rem)' }}
            >
              <InternalDemo />
            </motion.div>
          )}

          {activeView === 'demo-calendar' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="min-h-full w-full"
              style={{ padding: 'clamp(0.75rem, 1.5vw, 1.5rem)' }}
            >
              <DemoCalendarView />
            </motion.div>
          )}

          {activeView === 'load-test' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="min-h-full w-full"
              style={{ padding: 'clamp(0.75rem, 1.5vw, 1.5rem)' }}
            >
              <InternalLoadTest />
            </motion.div>
          )}

          {activeView === 'testing-check' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="min-h-full w-full"
              style={{ padding: 'clamp(0.75rem, 1.5vw, 1.5rem)' }}
            >
              <InternalTestingCheck />
            </motion.div>
          )}

          {activeView === 'ai-healer' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="min-h-full w-full"
              style={{ padding: 'clamp(0.75rem, 1.5vw, 1.5rem)' }}
            >
              <AIHealerAdmin />
            </motion.div>
          )}

          {activeView === 'blogs' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="min-h-full w-full"
              style={{ padding: 'clamp(0.75rem, 1.5vw, 1.5rem)' }}
            >
              <InternalBlog />
            </motion.div>
          )}

          {activeView === 'packages' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="min-h-full w-full"
              style={{ padding: 'clamp(0.75rem, 1.5vw, 1.5rem)' }}
            >
              <InternalPackages />
            </motion.div>
          )}

          {activeView === 'client-config' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="min-h-full w-full"
              style={{ padding: 'clamp(0.75rem, 1.5vw, 1.5rem)' }}
            >
              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-2">Client Configurations</h2>
                <p className="text-sm text-gray-600 mb-4">Manage feature flags, AI models, and templates for each client organisation.</p>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-sm font-medium text-gray-700">Select an organisation from the Dashboard to configure it.</p>
                  <button
                    type="button"
                    onClick={() => setActiveView('dashboard')}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Go to Dashboard
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {activeView === 'create-organisation' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="min-h-full w-full bg-gradient-to-b from-gray-50 to-gray-100/50"
            >
              <div className="w-full px-4 sm:px-6 lg:px-8 pt-6 pb-12">
                <div className="flex items-center justify-between gap-4 mb-6">
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                      Create organisation
                    </h1>
                    <p className="mt-1.5 text-gray-600 text-sm sm:text-base">
                      Add a new organisation with the details below. Hold{' '}
                      <kbd className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 font-mono text-xs">
                        Ctrl
                      </kbd>
                      +
                      <kbd className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 font-mono text-xs">
                        Shift
                      </kbd>{' '}
                      to show &quot;Fill random values&quot;.
                    </p>
                  </div>
                  {showFillRandomButton && (
                    <button
                      type="button"
                      onClick={fillRandomValues}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-amber-400 bg-amber-50 text-amber-800 font-medium hover:bg-amber-100 transition shrink-0"
                    >
                      <Sparkles className="w-5 h-5" />
                      Fill random values
                    </button>
                  )}
                </div>

                <form
                  onSubmit={handleCreateOrgSubmit}
                  className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden"
                >
                  <div className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
                      <div className="space-y-6">
                        <section>
                          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                            Basic details
                          </h2>
                          <div className="space-y-4">
                            <div>
                              <label htmlFor="org-name" className={labelCls}>
                                Organisation name <span className="text-red-500">*</span>
                              </label>
                              <input
                                id="org-name"
                                type="text"
                                required
                                value={orgForm.name}
                                onChange={(e) => {
                                  const name = e.target.value;
                                  setOrgForm((prev) => ({
                                    ...prev,
                                    name,
                                    slug: prev.slug || generateOrgSlug(name),
                                  }));
                                  setOrgFieldErrors((prev) => {
                                    if (!prev || !prev.name) return prev;
                                    const next = { ...prev };
                                    delete next.name;
                                    return next;
                                  });
                                }}
                                placeholder="e.g. Acme Inc."
                                className={cn(
                                  inputCls,
                                  orgFieldErrors?.name &&
                                  'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                )}
                              />
                              {orgFieldErrors?.name && (
                                <p className="mt-1 text-[11px] text-red-600">
                                  Organisation name: {orgFieldErrors.name}
                                </p>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label htmlFor="org-slug" className={labelCls}>
                                  Slug
                                </label>
                                <input
                                  id="org-slug"
                                  type="text"
                                  value={orgForm.slug}
                                  disabled
                                  placeholder="auto-generated-from-name"
                                  className={cn(inputCls, 'bg-gray-100 cursor-not-allowed')}
                                />
                              </div>
                              <div>
                                <label htmlFor="org-legalName" className={labelCls}>
                                  Legal name
                                </label>
                                <input
                                  id="org-legalName"
                                  type="text"
                                  value={orgForm.legalName}
                                  onChange={(e) => updateOrgForm('legalName', e.target.value)}
                                  placeholder="Registered name"
                                  className={inputCls}
                                />
                              </div>
                            </div>
                          </div>
                        </section>

                        <section className="pt-6 border-t border-gray-100">
                          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                            Contact
                          </h2>
                          <div className="space-y-4">
                            <div>
                              <label htmlFor="org-email" className={labelCls}>
                                Email <span className="text-red-500">*</span>
                              </label>
                              <input
                                id="org-email"
                                type="email"
                                required
                                value={orgForm.email}
                                onChange={(e) => updateOrgForm('email', e.target.value)}
                                placeholder="contact@company.com"
                                className={cn(
                                  inputCls,
                                  orgFieldErrors?.email &&
                                  'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                )}
                              />
                              {orgFieldErrors?.email && (
                                <p className="mt-1 text-[11px] text-red-600">
                                  Email: {orgFieldErrors.email}
                                </p>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label htmlFor="org-phone" className={labelCls}>
                                  Phone
                                </label>
                                <input
                                  id="org-phone"
                                  type="tel"
                                  value={orgForm.phone}
                                  onChange={(e) => updateOrgForm('phone', e.target.value)}
                                  placeholder="+1 234 567 8900"
                                  className={inputCls}
                                />
                              </div>
                              <div>
                                <label htmlFor="org-website" className={labelCls}>
                                  Website
                                </label>
                                <input
                                  id="org-website"
                                  type="url"
                                  value={orgForm.website}
                                  onChange={(e) => updateOrgForm('website', e.target.value)}
                                  placeholder="https://company.com"
                                  className={inputCls}
                                />
                              </div>
                            </div>
                          </div>
                        </section>

                        <section className="pt-6 border-t border-gray-100">
                          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                            Org admin
                          </h2>
                          <p className="text-sm text-gray-500 mb-4">
                            An activation email will be sent to the contact email to set password and
                            sign in.
                          </p>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label htmlFor="org-firstName" className={labelCls}>
                                First name
                              </label>
                              <input
                                id="org-firstName"
                                type="text"
                                value={orgForm.firstName}
                                onChange={(e) => updateOrgForm('firstName', e.target.value)}
                                placeholder="Admin first name"
                                className={inputCls}
                              />
                            </div>
                            <div>
                              <label htmlFor="org-lastName" className={labelCls}>
                                Last name
                              </label>
                              <input
                                id="org-lastName"
                                type="text"
                                value={orgForm.lastName}
                                onChange={(e) => updateOrgForm('lastName', e.target.value)}
                                placeholder="Admin last name"
                                className={inputCls}
                              />
                            </div>
                          </div>
                        </section>
                      </div>

                      <div className="space-y-6">
                        <section>
                          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                            Address
                          </h2>
                          <div className="space-y-4">
                            <div>
                              <label htmlFor="org-addressLine1" className={labelCls}>
                                Address line 1
                              </label>
                              <input
                                id="org-addressLine1"
                                type="text"
                                value={orgForm.addressLine1}
                                onChange={(e) => updateOrgForm('addressLine1', e.target.value)}
                                placeholder="Street, building"
                                className={inputCls}
                              />
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                              <div>
                                <label htmlFor="org-city" className={labelCls}>
                                  City
                                </label>
                                <input
                                  id="org-city"
                                  type="text"
                                  value={orgForm.city}
                                  onChange={(e) => updateOrgForm('city', e.target.value)}
                                  placeholder="City"
                                  className={inputCls}
                                />
                              </div>
                              <div>
                                <label htmlFor="org-state" className={labelCls}>
                                  State
                                </label>
                                <input
                                  id="org-state"
                                  type="text"
                                  value={orgForm.state}
                                  onChange={(e) => updateOrgForm('state', e.target.value)}
                                  placeholder="State"
                                  className={inputCls}
                                />
                              </div>
                              <div>
                                <label htmlFor="org-postalCode" className={labelCls}>
                                  Postal
                                </label>
                                <input
                                  id="org-postalCode"
                                  type="text"
                                  value={orgForm.postalCode}
                                  onChange={(e) => updateOrgForm('postalCode', e.target.value)}
                                  placeholder="ZIP"
                                  className={inputCls}
                                />
                              </div>
                            </div>
                            <div>
                              <label htmlFor="org-country" className={labelCls}>
                                Country
                              </label>
                              <select
                                id="org-country"
                                value={orgForm.country}
                                onChange={(e) => updateOrgForm('country', e.target.value)}
                                className={inputCls}
                              >
                                {COUNTRIES.map((c) => (
                                  <option key={c.value} value={c.value}>
                                    {c.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </section>

                        <section className="pt-6 border-t border-gray-100">
                          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                            Business
                          </h2>
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label htmlFor="org-industry" className={labelCls}>
                                  Industry
                                </label>
                                <input
                                  id="org-industry"
                                  type="text"
                                  value={orgForm.industry}
                                  onChange={(e) => updateOrgForm('industry', e.target.value)}
                                  placeholder="e.g. Technology"
                                  className={inputCls}
                                />
                              </div>
                              <div>
                                <label htmlFor="org-companySize" className={labelCls}>
                                  Company size
                                </label>
                                <select
                                  id="org-companySize"
                                  value={orgForm.companySize}
                                  onChange={(e) => updateOrgForm('companySize', e.target.value)}
                                  className={inputCls}
                                >
                                  {COMPANY_SIZES.map((s) => (
                                    <option key={s.value} value={s.value}>
                                      {s.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div>
                              <label htmlFor="org-description" className={labelCls}>
                                Description
                              </label>
                              <textarea
                                id="org-description"
                                rows={3}
                                value={orgForm.description}
                                onChange={(e) => updateOrgForm('description', e.target.value)}
                                placeholder="Brief description"
                                className={inputCls}
                              />
                            </div>
                          </div>
                        </section>
                      </div>
                    </div>

                    <section className="mt-8 pt-6 border-t border-gray-100">
                      <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                        Plan &amp; status
                      </h2>
                      <div className="flex flex-wrap gap-6">
                        <div className="w-48">
                          <label htmlFor="org-plan" className={labelCls}>
                            Plan
                          </label>
                          <select
                            id="org-plan"
                            value={orgForm.plan}
                            onChange={(e) => {
                              const value = e.target.value;
                              updateOrgForm('plan', value);
                              if (value !== 'core') {
                                updateOrgForm('totalAiCredits', '');
                              }
                              // Reset quota overrides when switching to a known plan
                              if (value === 'starter' || value === 'core') {
                                updateOrgForm('quotaVideoInterviews', '');
                                updateOrgForm('quotaAudioInterviews', '');
                                updateOrgForm('quotaAssessments', '');
                                updateOrgForm('quotaResumeScores', '');
                                updateOrgForm('planTotalUsers', '');
                              }
                            }}
                            className={inputCls}
                          >
                            {PLANS.map((p) => (
                              <option key={p.value} value={p.value}>
                                {p.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="w-48">
                          <label htmlFor="org-billing-period" className={labelCls}>
                            Billing period
                          </label>
                          <select
                            id="org-billing-period"
                            value={orgForm.billingPeriod}
                            onChange={(e) => updateOrgForm('billingPeriod', e.target.value)}
                            className={inputCls}
                          >
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="yearly">Yearly</option>
                          </select>
                        </div>
                        <div className="w-48">
                          <label htmlFor="org-status" className={labelCls}>
                            Status
                          </label>
                          <select
                            id="org-status"
                            value={orgForm.status}
                            onChange={(e) => updateOrgForm('status', e.target.value)}
                            className={inputCls}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                        <div className="w-full lg:w-80">
                          <label htmlFor="org-invoice" className={labelCls}>
                            Invoice (PDF or image)
                          </label>
                          <input
                            id="org-invoice"
                            type="file"
                            accept=".pdf,image/*"
                            onChange={(e) => {
                              const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                              setOrgInvoiceFile(file);
                            }}
                            className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-900 file:px-4 file:py-2.5 file:text-sm file:font-medium file:text-white hover:file:bg-gray-800 cursor-pointer"
                          />
                          {orgInvoiceFile && (
                            <p className="mt-1 text-xs text-gray-500">
                              Selected: {orgInvoiceFile.name}
                              {orgInvoiceUploading ? ' (uploading after org creation…)' : ''}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Plan-derived quotas info box */}
                      {PLAN_QUOTAS[orgForm.plan] && (
                        <div className="mt-4 rounded-xl bg-blue-50/60 border border-blue-200/60 p-4">
                          <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-2">
                            Included with {orgForm.plan === 'starter' ? 'Starter' : 'Core'} plan
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {[
                              { label: 'Credits', val: PLAN_QUOTAS[orgForm.plan].credits },
                              { label: 'Team Users', val: PLAN_QUOTAS[orgForm.plan].users },
                              { label: '1 Interview', val: '1 credit' },
                              { label: '1 Assessment', val: '0.5 credit' },
                            ].map((q) => (
                              <div key={q.label} className="text-center">
                                <p className="text-lg font-bold text-blue-900 tabular-nums">{typeof q.val === 'number' ? q.val.toLocaleString() : q.val}</p>
                                <p className="text-[10px] text-blue-600">{q.label}</p>
                              </div>
                            ))}
                          </div>
                          {PLAN_RATES[orgForm.plan] && (
                            <div className="mt-3 pt-2 border-t border-blue-200/50">
                              <p className="text-[11px] text-blue-600 mb-1">PAYG on-demand rates (after credits exhausted):</p>
                              <div className="flex flex-wrap gap-3 text-[11px] text-blue-700">
                                <span>Interview: ₹{PLAN_RATES[orgForm.plan].interview}/unit</span>
                                <span>Assessment: ₹{PLAN_RATES[orgForm.plan].assessment}/unit</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Custom plan: quota overrides */}
                      {orgForm.plan === 'custom' && (
                        <div className="mt-4 rounded-xl bg-gray-50 border border-gray-200 p-4">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">
                            Custom quota allocation
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                            <div>
                              <label className="block text-[11px] text-gray-500 mb-1">Video Interviews</label>
                              <input type="number" min="0" value={orgForm.quotaVideoInterviews} onChange={(e) => updateOrgForm('quotaVideoInterviews', e.target.value)} className={inputCls} placeholder="e.g. 100" />
                            </div>
                            <div>
                              <label className="block text-[11px] text-gray-500 mb-1">Audio Interviews</label>
                              <input type="number" min="0" value={orgForm.quotaAudioInterviews} onChange={(e) => updateOrgForm('quotaAudioInterviews', e.target.value)} className={inputCls} placeholder="e.g. 100" />
                            </div>
                            <div>
                              <label className="block text-[11px] text-gray-500 mb-1">Assessments</label>
                              <input type="number" min="0" value={orgForm.quotaAssessments} onChange={(e) => updateOrgForm('quotaAssessments', e.target.value)} className={inputCls} placeholder="e.g. 200" />
                            </div>
                            <div>
                              <label className="block text-[11px] text-gray-500 mb-1">Resume Scores</label>
                              <input type="number" min="0" value={orgForm.quotaResumeScores} onChange={(e) => updateOrgForm('quotaResumeScores', e.target.value)} className={inputCls} placeholder="e.g. 1000" />
                            </div>
                            <div>
                              <label className="block text-[11px] text-gray-500 mb-1">Team Users</label>
                              <input type="number" min="0" value={orgForm.planTotalUsers} onChange={(e) => updateOrgForm('planTotalUsers', e.target.value)} className={inputCls} placeholder="e.g. 15" />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Credit Cost Overrides */}
                      <div className="mt-4 rounded-xl bg-gray-50 border border-gray-200 p-4">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">
                          Credit Cost Settings
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-[11px] text-gray-500 mb-1">
                              AI Interview Credit Cost (Cr)
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              required
                              value={orgForm.creditCostInterview}
                              onChange={(e) => updateOrgForm('creditCostInterview', e.target.value)}
                              className={inputCls}
                              placeholder="Default: 1.0"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] text-gray-500 mb-1">
                              Custom Assessment Credit Cost (Cr)
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              required
                              value={orgForm.creditCostAssessment}
                              onChange={(e) => updateOrgForm('creditCostAssessment', e.target.value)}
                              className={inputCls}
                              placeholder="Default: 0.5"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] text-gray-500 mb-1">
                              Resume Screening Credit Cost (Cr)
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              required
                              value={orgForm.creditCostResume}
                              onChange={(e) => updateOrgForm('creditCostResume', e.target.value)}
                              className={inputCls}
                              placeholder="Default: 0.0 (Free)"
                            />
                          </div>
                        </div>
                      </div>

                      {/* PAYG settings */}
                      <div className="mt-4 rounded-xl bg-gray-50 border border-gray-200 p-4">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">
                          Pay-as-you-go (PAYG) settings
                        </p>
                        <div className="flex flex-wrap items-center gap-6">
                          <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={orgForm.ondemandEnabled}
                              onChange={(e) => updateOrgForm('ondemandEnabled', e.target.checked)}
                              className={cn(
                                'w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer',
                                orgFieldErrors?.ondemandEnabled && 'border-red-500 focus:ring-red-500/20 text-red-600'
                              )}
                            />
                            <span className="text-sm text-gray-700">Enable PAYG (bill for overages)</span>
                          </label>
                          {orgFieldErrors?.ondemandEnabled && (
                            <p className="w-full text-[11px] text-red-600 -mt-2">
                              Enable PAYG: {orgFieldErrors.ondemandEnabled}
                            </p>
                          )}
                          <div className="w-52">
                            <label className="block text-[11px] text-gray-500 mb-1">Monthly spend limit (₹)</label>
                            <input
                              type="number"
                              min="0"
                              value={orgForm.ondemandLimit}
                              onChange={(e) => updateOrgForm('ondemandLimit', e.target.value)}
                              placeholder="No limit"
                              className={inputCls}
                              disabled={!orgForm.ondemandEnabled}
                            />
                          </div>
                        </div>

                        {/* Custom on-demand rates (only for custom/enterprise plans) */}
                        {orgForm.plan === 'custom' && orgForm.ondemandEnabled && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-[11px] text-gray-500 mb-2">Custom on-demand rates (₹ per unit):</p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                              <div>
                                <label className="block text-[11px] text-gray-500 mb-1">Video Interview</label>
                                <input type="number" min="0" step="0.01" value={orgForm.ondemandRateVideo} onChange={(e) => updateOrgForm('ondemandRateVideo', e.target.value)} className={inputCls} placeholder="e.g. 149" />
                              </div>
                              <div>
                                <label className="block text-[11px] text-gray-500 mb-1">Audio Interview</label>
                                <input type="number" min="0" step="0.01" value={orgForm.ondemandRateAudio} onChange={(e) => updateOrgForm('ondemandRateAudio', e.target.value)} className={inputCls} placeholder="e.g. 89" />
                              </div>
                              <div>
                                <label className="block text-[11px] text-gray-500 mb-1">Assessment</label>
                                <input type="number" min="0" step="0.01" value={orgForm.ondemandRateAssessment} onChange={(e) => updateOrgForm('ondemandRateAssessment', e.target.value)} className={inputCls} placeholder="e.g. 15" />
                              </div>
                              <div>
                                <label className="block text-[11px] text-gray-500 mb-1">Resume Score</label>
                                <input type="number" min="0" step="0.01" value={orgForm.ondemandRateResume} onChange={(e) => updateOrgForm('ondemandRateResume', e.target.value)} className={inputCls} placeholder="e.g. 5" />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </section>
                  </div>

                  <div className="px-8 py-6 bg-gray-50/80 border-t border-gray-100 flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setOrgForm(INITIAL_ORG_FORM);
                        setOrgSubmitMessage(null);
                        setOrgFieldErrors(null);
                      }}
                      className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-white border border-gray-200 rounded-lg transition"
                    >
                      Clear
                    </button>
                    <button
                      type="submit"
                      disabled={orgSubmitLoading}
                      className="px-6 py-2.5 text-sm font-semibold text-white bg-[#2563eb] hover:bg-[#1d4ed8] rounded-lg shadow-sm hover:shadow transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {orgSubmitLoading ? 'Creating…' : 'Create organisation'}
                    </button>
                  </div>

                  {orgSubmitMessage && (
                    <div className="px-8 pb-8">
                      <div className="pt-6 border-t border-gray-100">
                        <p className="text-xs font-medium text-gray-500 mb-2">
                          Submitted (frontend only). Payload for backend:
                        </p>
                        <pre className="text-xs text-gray-600 overflow-auto max-h-40 rounded-lg bg-gray-50 p-3 font-mono whitespace-pre-wrap break-words">
                          {orgSubmitMessage}
                        </pre>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </motion.div>
          )}

          {activeView === 'support' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="min-h-full w-full bg-gradient-to-b from-gray-50 to-gray-100/50"
            >
              <div className="w-full px-4 sm:px-6 lg:px-8 pt-6 pb-12">
                <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                      Support requests
                    </h1>
                    <p className="mt-1.5 text-gray-600 text-sm sm:text-base">
                      View and resolve support queries from users.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={supportFilters.status}
                      onChange={(e) =>
                        setSupportFilters((prev) => ({ ...prev, status: e.target.value }))
                      }
                      className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    >
                      <option value="">All statuses</option>
                      <option value="open">Open</option>
                      <option value="in_progress">In progress</option>
                      <option value="closed">Closed</option>
                    </select>
                    <select
                      value={supportFilters.category}
                      onChange={(e) =>
                        setSupportFilters((prev) => ({ ...prev, category: e.target.value }))
                      }
                      className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    >
                      <option value="">All categories</option>
                      <option value="Billing">Billing</option>
                      <option value="Technical">Technical</option>
                      <option value="Account">Account</option>
                      <option value="Other">Other</option>
                    </select>
                    <select
                      value={supportFilters.priority}
                      onChange={(e) =>
                        setSupportFilters((prev) => ({ ...prev, priority: e.target.value }))
                      }
                      className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    >
                      <option value="">All priorities</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>

                {supportError && (
                  <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-700 text-sm mb-6">
                    {supportError}
                  </div>
                )}

                {supportLoading && (
                  <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent" />
                  </div>
                )}

                {!supportLoading && (
                  <div className="flex flex-col lg:flex-row gap-6">
                    <div className="flex-1 min-w-0 rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          {supportTotal} request{supportTotal !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm">
                          <thead className="border-b border-gray-100 bg-gray-50/80 text-gray-500">
                            <tr>
                              <th className="py-2.5 pr-3 pl-4 font-medium">ID</th>
                              <th className="py-2.5 px-3 font-medium">Title</th>
                              <th className="py-2.5 px-3 font-medium hidden sm:table-cell">Category</th>
                              <th className="py-2.5 px-3 font-medium hidden md:table-cell">Priority</th>
                              <th className="py-2.5 px-3 font-medium">Status</th>
                              <th className="py-2.5 pl-3 pr-4 font-medium">Created</th>
                            </tr>
                          </thead>
                          <tbody>
                            {supportRequests.map((req) => {
                              const creator = req.createdBy;
                              const name = creator?.name || creator?.email || '—';
                              return (
                                <tr
                                  key={req._id}
                                  onClick={() => fetchSupportDetail(req._id)}
                                  className={cn(
                                    'border-b border-gray-50 last:border-0 cursor-pointer transition',
                                    supportDetail?._id === req._id
                                      ? 'bg-blue-50'
                                      : 'hover:bg-gray-50'
                                  )}
                                >
                                  <td className="py-2.5 pr-3 pl-4 font-mono text-xs text-gray-600">
                                    {req.requestId || req._id}
                                  </td>
                                  <td className="py-2.5 px-3">
                                    <div className="flex flex-col">
                                      <span className="font-medium text-gray-900 truncate max-w-[180px]">
                                        {req.title || '—'}
                                      </span>
                                      <span className="text-xs text-gray-500 truncate max-w-[180px]">
                                        {name}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="py-2.5 px-3 text-gray-600 hidden sm:table-cell">
                                    {req.category || '—'}
                                  </td>
                                  <td className="py-2.5 px-3 hidden md:table-cell capitalize text-gray-600">
                                    {req.priority || '—'}
                                  </td>
                                  <td className="py-2.5 px-3">
                                    <span
                                      className={cn(
                                        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                                        req.status === 'closed'
                                          ? 'bg-gray-100 text-gray-600'
                                          : req.status === 'in_progress'
                                            ? 'bg-amber-50 text-amber-700'
                                            : 'bg-blue-50 text-blue-700'
                                      )}
                                    >
                                      {req.status === 'in_progress' ? 'In progress' : req.status}
                                    </span>
                                  </td>
                                  <td className="py-2.5 pl-3 pr-4 text-xs text-gray-500">
                                    {req.createdAt
                                      ? new Date(req.createdAt).toLocaleDateString()
                                      : '—'}
                                  </td>
                                </tr>
                              );
                            })}
                            {supportRequests.length === 0 && (
                              <tr>
                                <td
                                  colSpan={6}
                                  className="py-10 text-center text-gray-500 text-sm"
                                >
                                  No support requests found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      {supportDetailLoading && (
                        <div className="rounded-2xl border border-gray-200 bg-white p-8 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
                        </div>
                      )}
                      {!supportDetailLoading && supportDetail && (
                        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/80">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Request detail
                            </p>
                            <p className="font-mono text-sm text-gray-700 mt-0.5">
                              {supportDetail.requestId}
                            </p>
                          </div>
                          <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Title</p>
                              <p className="text-gray-900 font-medium">
                                {supportDetail.title || '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                {supportDetail.description || '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Opened by</p>
                              <p className="text-sm text-gray-700">
                                {supportAuthorName(supportDetail.createdBy)} on{' '}
                                {supportFormatDateTime(supportDetail.createdAt)}
                              </p>
                            </div>
                            {(supportDetail.inProgressAt || supportDetail.closedAt) && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">Timeline</p>
                                <div className="text-sm text-gray-700 space-y-1">
                                  {supportDetail.inProgressAt && (
                                    <p>
                                      Set to In progress by{' '}
                                      {supportDetail.inProgressBy
                                        ? supportAuthorName(supportDetail.inProgressBy)
                                        : 'Support'}{' '}
                                      on {supportFormatDateTime(supportDetail.inProgressAt)}
                                    </p>
                                  )}
                                  {supportDetail.closedAt && (
                                    <p>
                                      Closed on {supportFormatDateTime(supportDetail.closedAt)}
                                      {supportDetail.resolvedBy &&
                                        ` by ${supportAuthorName(supportDetail.resolvedBy)}`}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                            <div className="flex flex-wrap gap-2 text-xs">
                              <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-700">
                                {supportDetail.category}
                              </span>
                              <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-700 capitalize">
                                {supportDetail.priority}
                              </span>
                            </div>

                            {supportDetail.comments && supportDetail.comments.length > 0 && (
                              <div className="pt-3 border-t border-gray-100">
                                <p className="text-xs font-medium text-gray-500 mb-2">Comments</p>
                                <div className="space-y-2">
                                  {supportDetail.comments.map((c) => {
                                    const isCurrentUser =
                                      user &&
                                      c.author &&
                                      String(c.author._id) === String(user._id ?? user.id);
                                    return (
                                      <div
                                        key={c._id || c.createdAt}
                                        className={cn(
                                          'rounded-lg p-2.5 text-sm max-w-[85%]',
                                          isCurrentUser
                                            ? 'ml-auto bg-sky-50 border border-sky-100'
                                            : 'mr-auto bg-gray-50 border border-gray-100'
                                        )}
                                      >
                                        <p className="text-xs font-medium text-gray-600 mb-1">
                                          {isCurrentUser ? 'You' : supportAuthorName(c.author)} ·{' '}
                                          {supportFormatDateTime(c.createdAt)}
                                        </p>
                                        <p className="text-gray-900 whitespace-pre-wrap">{c.text}</p>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            <div className="pt-3 border-t border-gray-100">
                              <p className="text-xs font-medium text-gray-500 mb-2">Status</p>
                              <div className="flex flex-wrap gap-2">
                                {['open', 'in_progress', 'closed'].map((s) => (
                                  <button
                                    key={s}
                                    type="button"
                                    disabled={supportSaving || supportDetail.status === s}
                                    onClick={() => updateSupportStatus(supportDetail._id, s)}
                                    className={cn(
                                      'px-3 py-1.5 rounded-lg text-sm font-medium transition',
                                      supportDetail.status === s
                                        ? 'bg-blue-600 text-white cursor-default'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50'
                                    )}
                                  >
                                    {s === 'in_progress' ? 'In progress' : s}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="pt-3 border-t border-gray-100">
                              <label className="block text-xs font-medium text-gray-500 mb-2">
                                Add reply (user will see this)
                              </label>
                              <textarea
                                value={supportReplyText}
                                onChange={(e) => setSupportReplyText(e.target.value)}
                                rows={3}
                                placeholder="Ask a question or add a note for the user..."
                                className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:ring-1 focus:ring-blue-500 outline-none resize-y"
                              />
                              <button
                                type="button"
                                disabled={supportReplySubmitting || !supportReplyText.trim()}
                                onClick={() => submitSupportReply(supportDetail._id)}
                                className="mt-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-[#2563eb] text-white hover:bg-[#1d4ed8] disabled:opacity-50"
                              >
                                {supportReplySubmitting ? 'Sending…' : 'Send reply'}
                              </button>
                            </div>

                            <div className="pt-3 border-t border-gray-100">
                              <label className="block text-xs font-medium text-gray-500 mb-2">
                                Internal notes (not visible to user)
                              </label>
                              <textarea
                                value={supportNotesLocal}
                                onChange={(e) => setSupportNotesLocal(e.target.value)}
                                rows={3}
                                placeholder="Private scratch notes"
                                className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:ring-1 focus:ring-blue-500 outline-none resize-y"
                              />
                              <button
                                type="button"
                                disabled={supportSaving}
                                onClick={() => saveSupportNotes(supportDetail._id)}
                                className="mt-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-600 text-white hover:bg-gray-700 disabled:opacity-50"
                              >
                                {supportSaving ? 'Saving…' : 'Save notes'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      {!supportDetailLoading && !supportDetail && supportRequests.length > 0 && (
                        <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-500 text-sm">
                          Select a request to view details and update status.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeView === 'resources-admin' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="min-h-full w-full bg-gradient-to-b from-gray-50 to-gray-100/50"
            >
              <div className="w-full px-4 sm:px-6 lg:px-8 pt-6 pb-12">
                <div className="mb-8">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                    Resources (videos)
                  </h1>
                  <p className="mt-1.5 text-gray-600 text-sm sm:text-base">
                    Add video lessons with thumbnail, title, duration, level and tags. Files are uploaded to S3 and shown on the Resources page.
                  </p>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 max-w-2xl mb-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Add new video</h2>
                  {resourceSubmitError && (
                    <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                      {resourceSubmitError}
                    </div>
                  )}
                  <form onSubmit={handleResourceSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                      <input
                        type="text"
                        value={resourceForm.title}
                        onChange={(e) => setResourceForm((p) => ({ ...p, title: e.target.value }))}
                        placeholder="e.g. Platform overview in 7 minutes"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF] outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                      <textarea
                        value={resourceForm.description}
                        onChange={(e) => setResourceForm((p) => ({ ...p, description: e.target.value }))}
                        placeholder="Short description"
                        rows={2}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF] outline-none"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes) *</label>
                        <input
                          type="number"
                          min={0}
                          value={resourceForm.minutes}
                          onChange={(e) => setResourceForm((p) => ({ ...p, minutes: e.target.value }))}
                          placeholder="7"
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
                        <select
                          value={resourceForm.level}
                          onChange={(e) => setResourceForm((p) => ({ ...p, level: e.target.value }))}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF] outline-none"
                        >
                          <option value="Beginner">Beginner</option>
                          <option value="Intermediate">Intermediate</option>
                          <option value="Advanced">Advanced</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <select
                        value={resourceForm.category}
                        onChange={(e) => setResourceForm((p) => ({ ...p, category: e.target.value }))}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF] outline-none"
                      >
                        <option value="getting-started">Getting started</option>
                        <option value="job-posts">Job posts & screenings</option>
                        <option value="results">Results & analytics</option>
                        <option value="teams">Teams & access</option>
                        <option value="library">Library</option>
                        <option value="support-requests">Support requests</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                      <input
                        type="text"
                        value={resourceForm.tags}
                        onChange={(e) => setResourceForm((p) => ({ ...p, tags: e.target.value }))}
                        placeholder="e.g. overview, setup, first-job"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF] outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Thumbnail (optional, image)</label>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={(e) => setResourceThumbnail(e.target.files?.[0] || null)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 file:mr-2 file:rounded file:border-0 file:bg-[#007BFF]/10 file:px-3 file:py-1 file:text-sm file:font-medium file:text-[#007BFF]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Video file * (MP4, WebM)</label>
                      <input
                        type="file"
                        accept="video/mp4,video/webm,video/quicktime"
                        onChange={(e) => setResourceVideoFile(e.target.files?.[0] || null)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 file:mr-2 file:rounded file:border-0 file:bg-[#007BFF]/10 file:px-3 file:py-1 file:text-sm file:font-medium file:text-[#007BFF]"
                      />
                      {resourceVideoFile && (
                        <p className="mt-1 text-xs text-gray-500">{resourceVideoFile.name}</p>
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={resourceSubmitLoading}
                      className="rounded-lg bg-[#007BFF] px-4 py-2 text-sm font-medium text-white hover:bg-[#0066cc] disabled:opacity-50"
                    >
                      {resourceSubmitLoading ? 'Uploading…' : 'Add video'}
                    </button>
                  </form>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Existing videos</span>
                    <button
                      type="button"
                      onClick={fetchResourceVideos}
                      disabled={resourceVideosLoading}
                      className="text-xs text-[#007BFF] hover:underline disabled:opacity-50"
                    >
                      Refresh
                    </button>
                  </div>
                  {resourceVideosLoading && (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#007BFF] border-t-transparent" />
                    </div>
                  )}
                  {!resourceVideosLoading && resourceVideos.length === 0 && (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">No videos yet. Add one above.</div>
                  )}
                  {!resourceVideosLoading && resourceVideos.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-left text-sm">
                        <thead className="border-b border-gray-100 bg-gray-50/80 text-gray-500">
                          <tr>
                            <th className="py-2.5 pl-4 pr-2 font-medium">Title</th>
                            <th className="py-2.5 px-2 font-medium">Min</th>
                            <th className="py-2.5 px-2 font-medium">Level</th>
                            <th className="py-2.5 px-2 font-medium">Category</th>
                            <th className="py-2.5 pl-2 pr-4 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resourceVideos.map((v) => (
                            <tr key={v._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                              <td className="py-2.5 pl-4 pr-2 font-medium text-gray-900 truncate max-w-[200px]">{v.title}</td>
                              <td className="py-2.5 px-2 text-gray-600">{v.minutes}</td>
                              <td className="py-2.5 px-2 text-gray-600">{v.level}</td>
                              <td className="py-2.5 px-2 text-gray-600">{v.category || '—'}</td>
                              <td className="py-2.5 pl-2 pr-4">
                                <button
                                  type="button"
                                  onClick={() => handleResourceVideoDelete(v._id)}
                                  className="text-red-600 hover:underline text-xs"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </TooltipProvider>
      </main>
    </div>
  );
};

export default Internal;
